import { Box, Container, Hidden } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { formatPrice } from "../../../../../helpers/price";
import { useExpressDeliveryAvailabilitySelector } from "../../../../../hooks/use-express-delivery-availability";
import useFeatureFlag from "../../../../../hooks/use-feature-flag";
import useIsMobile from "../../../../../hooks/use-is-mobile";
import useTranslate from "../../../../../hooks/use-translate";
import theme from "../../../../../theme/material-ui-theme";
import { ProductType } from "../../../../../types/airgraft";
import ProductBuyingOptions from "../../../ProductBuyingOptions";
import {
  getProductInventoryLevel,
  useProductVariantByQueryParam
} from "../../utils";
import ProductDetailsActions from "../Actions";
import DisabledActionButton from "../Actions/DisabledActionButton";
import ProductDetailsGallery from "../Gallery";
import ProductDetailsStickyBar from "../StickyBar";
import ProductDetailsDeviceTable from "./DeviceTable";
import ProductDetailsOilTable from "./OilTable";
import { BrandTypography, PriceTypography, TitleTypography } from "./styles";

type Props = {
  product: ProductType;
  isModal?: boolean;
};

function ProductDetailsOverview({ product, isModal }: Props) {
  const t = useTranslate();
  const isRetailProvidersEnabled = useFeatureFlag("SHOP_RETAIL_PROVIDERS");
  const isXpressEnabled = useFeatureFlag("SHOP_XPRESS");
  const isPriceEnabled = useFeatureFlag("SHOP_PRICE");

  const isMobile = useIsMobile();
  const [productVariant] = useProductVariantByQueryParam(product);
  const getAvailableProviderProductData =
    useExpressDeliveryAvailabilitySelector(
      c => c.getAvailableProviderProductData
    );

  // Product Price
  const providerProductData = getAvailableProviderProductData(product);
  const productPrice = isPriceEnabled
    ? providerProductData?.price || {
        amount: productVariant.price,
        currencyCode: "USD"
      }
    : undefined;

  const productInventoryLevel = providerProductData
    ? getProductInventoryLevel(providerProductData.availableQuantity)
    : null;

  // Sticky bar
  const [titleRef, isTitleInView] = useInView({
    threshold: 0.5,
    initialInView: true
  });
  const [actionsRef, isActionsInView] = useInView({
    threshold: 0.5,
    initialInView: true
  });

  // Prevent <StickyBar> from being visible during modal opening animation.
  const [hasModalAnimationEnded, setAnimationModalEnded] = useState(false);
  if (isModal) {
    useEffect(() => {
      setTimeout(() => {
        setAnimationModalEnded(true);
      }, 350);
    }, []);
  }

  const isStickyBarVisible =
    hasModalAnimationEnded && (isMobile ? !isTitleInView : !isActionsInView);

  const isAirgraftBattery =
    product.productType === "device" &&
    productVariant.brand.slug === "airgraft";

  return (
    <Container maxWidth="md">
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "start" }}
      >
        {/* Image gallery */}
        <Box flex={1} mb={{ xs: 3, sm: 0 }} position="relative">
          <ProductDetailsGallery product={product} />
        </Box>
        {/* Product informations */}
        <Box flex={1} marginLeft={{ xs: 0, sm: 2, md: 4 }}>
          {/* Title + Brand */}
          <Box
            mb={{ xs: 2, sm: 3 }}
            textAlign={{ xs: "center", sm: "left" }}
            ref={titleRef}
          >
            {/* Product Variant Title */}
            <Box
              display={{ xs: "inline", sm: "block" }}
              mb={{ xs: 0, sm: 0.5 }}
            >
              <TitleTypography component="h1">
                {productVariant.title}
              </TitleTypography>
            </Box>

            {/* Product variant brand */}
            {!isAirgraftBattery && (
              <Box display={{ xs: "inline", sm: "block" }}>
                <BrandTypography
                  component="span"
                  style={{ color: theme.palette.grey[600] }}
                >
                  {` ${t("by")} `}
                </BrandTypography>
                <BrandTypography component="h2">
                  {productVariant.brand.name}
                </BrandTypography>
              </Box>
            )}
          </Box>

          {/* Special gray border for Airgraft 2 battery mobile pdp */}
          {isAirgraftBattery &&
            isXpressEnabled &&
            !isRetailProvidersEnabled && (
              <Box
                borderTop={`2px solid ${theme.palette.grey[400]}`}
                width="100%"
                display={{ xs: "block", sm: "none" }}
                marginBottom={2}
              />
            )}

          {/* Oil table */}
          {product.productType === "oil" && (
            <ProductDetailsOilTable
              product={product}
              price={productPrice}
              inventoryLevel={productInventoryLevel}
            />
          )}

          {/* Device table */}
          {product.productType === "device" && (
            <>
              <Hidden xsDown implementation="css">
                <ProductDetailsDeviceTable productVariant={productVariant} />
              </Hidden>

              {/* Price & Membership */}
              {productPrice && !isRetailProvidersEnabled && (
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  marginBottom={{ xs: 3, sm: 4 }}
                  marginTop={{ xs: 0, sm: 2 }}
                >
                  <PriceTypography>
                    {formatPrice(productPrice, {
                      hideCurrencyCode: true
                    })}
                  </PriceTypography>
                </Box>
              )}
            </>
          )}

          {/* Add to cart XPRESS buttons */}
          {productVariant.availability === "available" && (
            <div ref={actionsRef}>
              <ProductDetailsActions product={product} />
            </div>
          )}

          {/* Coming soon */}
          {productVariant.availability === "coming-soon" && (
            <DisabledActionButton>{t("shop.comingSoon")}</DisabledActionButton>
          )}

          {/* Sold Out */}
          {productVariant.availability === "sold-out" && (
            <DisabledActionButton>{t("pdp.soldOut")}</DisabledActionButton>
          )}

          {/* Device table (Mobile) */}
          {product.productType === "device" && (
            <Hidden smUp implementation="css">
              <Box mt={4}>
                <ProductDetailsDeviceTable productVariant={productVariant} />
              </Box>
            </Hidden>
          )}

          {/* "Buy at" options / Retail providers */}
          {isRetailProvidersEnabled &&
            productVariant.availability === "available" && (
              <ProductBuyingOptions product={product} />
            )}
        </Box>
      </Box>

      {/* Sticky bar */}
      <ProductDetailsStickyBar
        product={product}
        isVisible={isStickyBarVisible}
        isModal={isModal}
      />
    </Container>
  );
}

export default ProductDetailsOverview;
